import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import errorPages, { HeadlessErrorPage } from '@saucelabs/sl-component-error-pages';
import AnalyticsRoutes from '@saucelabs/web-ui-analytics/src/routes';
import ArchivesRoutes from '@saucelabs/web-ui-archives/src/routes';
import AuthRoutes from '@saucelabs/web-ui-auth/src/routes';
import BuildsRoutes from '@saucelabs/web-ui-builds/src/routes';
import PerformanceRoutes from '@saucelabs/web-ui-extended-debugging/src/routes';
import IntegrationsRoutes from '@saucelabs/web-ui-integrations/src/routes';
import InvitationsRoutes from '@saucelabs/web-ui-invitations/src/routes';
import JobRoutes from '@saucelabs/web-ui-job-details/src/routes';
import EmbeddedJobRoutes from '@saucelabs/web-ui-job-details/src/routes-embedded';
import JobsRoutes from '@saucelabs/web-ui-jobs/src/routes';
import JobsSubaccountRoutes from '@saucelabs/web-ui-jobs/src/routesSubaccount';
import JobsOpenSauceRoutes from '@saucelabs/web-ui-jobs/src/routesOpenSauce';
import AdminUserDashboardRoutes from '@saucelabs/web-ui-jobs/src/routesAdminUserDashboard';
import ManualRoutes from '@saucelabs/web-ui-manual/src/routes';
import ManualPublicRoutes from '@saucelabs/web-ui-manual/src/routesPublic';
import MigrationWizardRoutes from '@saucelabs/web-ui-migration-wizard/src/routes';
import TeamManagementRoutes from '@saucelabs/web-ui-team-management/src/routes';
import TunnelsRoutes from '@saucelabs/web-ui-tunnels/src/routes';
import UserSettingsRoutes from '@saucelabs/web-ui-user-settings/src/routes';
import { DashboardRoute } from '@saucelabs/sl-container-dashboard';
import { RedirectToDjango } from '@saucelabs/sl-container-experiment';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { LastLocationProvider } from 'react-router-last-location';

import ApplicationContainer from './application/containers/ApplicationContainer';
import store, { browserHistory } from './store';

const EmbeddedHeadlessErrorPage = () => <HeadlessErrorPage embedded />;
const isSupportedFeatureRoute = ({ name, routes }) => (
  isSupportedFeature(name) ? routes : EmbeddedHeadlessErrorPage
);


const router = (
  <Provider store={store}>
    <ConnectedRouter history={browserHistory}>
      <LastLocationProvider>
        <Switch>
          {Object.entries(errorPages).map(([code, ErrorPage]) => (
            <Route
              key={`error-${code}`}
              component={ErrorPage}
              path={`/${code}`}
            />
          ))}
          <Redirect
            exact
            from="/"
            to="/dashboard"
          />
          {/* legacy Open Sauce route */}
          <Redirect
            from="/u/:username"
            to="/open_sauce/user/:username"
          />
          {/* legacy live testing routes */}
          <Redirect
            exact
            from="/dashboard/manual"
            to="/dashboard/live"
          />
          {AuthRoutes}
          <Route
            component={InvitationsRoutes}
            path="/invitations/"
            viewName="Invitation"
          />
          {ManualPublicRoutes}
          {EmbeddedJobRoutes}
          <ApplicationContainer>
            <Switch>
              <DashboardRoute
                component={JobsOpenSauceRoutes}
                path="/open_sauce/"
                viewName="Dashboard"
              />
              <Route
                component={AdminUserDashboardRoutes}
                path="/admin/users/"
                viewName="Admin User's Dashoard"
              />
              <DashboardRoute
                component={TeamManagementRoutes}
                path="/team-management"
                viewName="Team Management"
              />
              <DashboardRoute
                component={isSupportedFeatureRoute({
                  name: features.ANALYTICS,
                  routes: AnalyticsRoutes,
                })}
                path="/analytics"
                viewName="Analytics"
              />
              <DashboardRoute
                component={isSupportedFeatureRoute({
                  name: features.ARCHIVES,
                  routes: ArchivesRoutes,
                })}
                path="/archives"
                viewName="Archives"
              />
              <DashboardRoute
                component={BuildsRoutes}
                path="/builds/"
                viewName="Builds"
              />
              <DashboardRoute
                component={JobsRoutes}
                path="/dashboard"
                viewName="Dashboard"
              />
              <DashboardRoute
                component={JobsSubaccountRoutes}
                path="/users/"
                viewName="Dashboard"
              />
              <DashboardRoute
                component={isSupportedFeatureRoute({
                  name: features.INTEGRATIONS,
                  routes: IntegrationsRoutes,
                })}
                path="/integrations"
                viewName="Integrations"
              />
              <DashboardRoute
                component={JobRoutes}
                path="/tests/"
                viewName="Job Details"
              />
              <DashboardRoute
                component={MigrationWizardRoutes}
                path="/migration-wizard"
                viewName="Migration Wizard"
              />
              <DashboardRoute
                component={PerformanceRoutes}
                path="/performance/"
                viewName="Performance"
              />
              <DashboardRoute
                component={TunnelsRoutes}
                path="/tunnels"
                viewName="Tunnels"
              />
              <DashboardRoute
                component={UserSettingsRoutes}
                path="/user-settings"
                viewName="UserSettings"
              />
              {isSupportedFeature(features.LIVE_TESTING) ? (
                <Route
                  component={ManualRoutes}
                  path="/live"
                  viewName="Live testing"
                />
              ) : (
                <DashboardRoute
                  component={EmbeddedHeadlessErrorPage}
                  path="/live"
                  viewName="Live testing"
                />
              )}
              {/* Redirect if there no Route match to switch to Django app */}
              <Route component={RedirectToDjango} />
            </Switch>
          </ApplicationContainer>
        </Switch>
      </LastLocationProvider>
    </ConnectedRouter>
  </Provider>
);

export default router;
